/** @format */

import { Box, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Close';
import * as React from 'react';
import CancelCreateTicketModal from './cancel';
import SuccessCreateTicket from './success';
import ErrorCreateTicket from './error';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/button';
import useAuth from 'src/hooks/useAuth';
import SelectComponent from 'src/components/select';
import { FEATURE_FLAGS, MAX_NUMBER_OF_FILES, VALIDATE_REGEX } from 'src/constants';
import { useDispatch, useSelector } from 'src/store';
import { clearTicketError, createTicket, getCompanyTickets, getTickets } from 'src/slices/tickets';
import UploadField from 'src/components/upload/upload';
import UpdateTicketLoading from './updateTicketLoading';
import { Tooltip } from 'src/components/tooltip';

const initPostData = {
  title: '',
  contactName: '',
  contactEmail: '',
  dealerAssociation: '',
  customerCompanyName: '',
  affectedEmailAddress: '',
  affectedVehicle: '',
  affectedDriver: '',
  softwareVersion: '',
  deviceserialnumber: '',
  vehicleRegistrationNumber: '',
  companyCardNumber: '',
  description: '',
  ava_endcustomer: '',
  ava_endcustomercontact: '',
  requestType: null,
  impact: null,
  urgency: null,
  typeOfProblem: null,
  component: null,
  feature: null,
  model: null,
  type: null,
  attachments: null,
  attachmentFileNames: null
};

export default function CreateTicketModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isOtherPerson, setIsOtherPerson] = React.useState(false);
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [postData, setPostData] = React.useState(initPostData);
  const [validations, setValidations] = React.useState({});
  const [componentData, setComponentData] = React.useState([]);
  const [files, setFiles] = React.useState([]);
  const [fileKey, setFileKey] = React.useState(Math.random().toString(36));
  const dispatch = useDispatch();
  const { userData } = useSelector(state => state.users);
  const { updateTicketLoading, error: errorTicket } = useSelector(state => state.tickets);
  const requestType = React.useMemo(() => {
    return {
      service: t('Tickets.requestTypeOption3'),
      sales: t('Tickets.requestTypeOption4'),
      logistics: t('Tickets.requestTypeOption5')
    };
  }, [t]);

  const hasTicketExtension = userData?.ticketExtension === true;

  const impactData = React.useMemo(() => {
    const base = [
      { value: 'Significant', name: t('Tickets.impactSignificant') },
      { value: 'Moderate', name: t('Tickets.impactModerate') },
      { value: 'Minor', name: t('Tickets.impactMinor') }
    ];
    if (hasTicketExtension && FEATURE_FLAGS.TICKET_EXTENSION) {
      return [{ value: 'Extensive', name: t('Tickets.impactExtensive') }, ...base];
    }
    return base;
  }, [t, hasTicketExtension]);

  const urgencyData = React.useMemo(() => {
    const base = [
      { value: 'High', name: t('Tickets.urgencyHigh') },
      { value: 'Medium', name: t('Tickets.urgencyMedium') },
      { value: 'Low', name: t('Tickets.urgencyLow') }
    ];
    if (hasTicketExtension) {
      return [{ value: 'Critical', name: t('Tickets.urgencyCritical') }, ...base];
    }
    return base;
  }, [t, hasTicketExtension]);

  React.useEffect(() => {
    if (errorTicket) setSuccess(false);
  }, [errorTicket]);

  React.useEffect(() => {
    if (userData) {
      restartProcess();
      if (userData.roles?.includes('admin')) setIsAdmin(true);
    }
  }, [open, user, userData]);
  React.useEffect(() => {
    if (success && !updateTicketLoading) {
      setPostData(initPostData);
    }
  }, [success, updateTicketLoading]);

  // Set component data dropdown
  React.useEffect(() => {
    setValidations({});
    switch (postData.typeOfProblem) {
      case 'VDO Fleet':
        setComponentData([
          { value: 'VDO Admin', name: t('Tickets.componentOption25') },
          { value: 'VDO Fleet Login', name: t('Tickets.componentOption1') },
          { value: 'Admin', name: t('Tickets.componentOption2') },
          { value: 'Connect', name: t('Tickets.componentOption3') },
          { value: 'VDO Fleet App', name: t('Tickets.componentOption4') },
          { value: 'Extract', name: t('Tickets.componentOption5') },
          { value: 'LiveData', name: t('Tickets.componentOption6') },
          { value: 'LiveMaps (Mobile)', name: t('Tickets.componentOption7') },
          { value: 'Motion', name: t('Tickets.componentOption8') },
          { value: 'Tachograph Management', name: t('Tickets.componentOption9') },
          { value: 'Remote Download', name: t('Tickets.componentOption10') },
          { value: 'Partner API', name: t('Tickets.componentOption28') }
          // { value: 'Local Products', name: t('Tickets.componentOption29') }
        ]);
        break;
      case 'Hardware':
        setComponentData([
          { value: 'VDO Link', name: t('Tickets.componentOption26') },
          { value: 'R-DL 4G', name: t('Tickets.componentOption11') },
          { value: 'DLD SR', name: t('Tickets.componentOption12') },
          { value: 'DLD WR', name: t('Tickets.componentOption13') },
          { value: 'Downloadkey', name: t('Tickets.componentOption14') },
          { value: 'ProConnect', name: t('Tickets.componentOption15') },
          { value: 'Smart Link', name: t('Tickets.componentOption16') },
          { value: 'Smart Terminal', name: t('Tickets.componentOption17') },
          { value: 'Tachograph Analog', name: t('Tickets.componentOption18') },
          { value: 'Tachograph Digital', name: t('Tickets.componentOption19') },
          { value: 'DTCO Simulator', name: t('Tickets.componentOption20') },
          { value: 'Kitas / M1N1', name: t('Tickets.componentOption21') },
          { value: 'CTC', name: t('Tickets.componentOption22') },
          { value: 'WorkshopKey', name: t('Tickets.componentOption23') },
          { value: 'WorkshopTablet', name: t('Tickets.componentOption24') }
          // { value: 'Telematic Services', name: t('Tickets.componentOption27') }
        ]);
        break;
      default:
        break;
    }
  }, [postData.typeOfProblem]);
  // Change isOtherPerson checkbox
  React.useEffect(() => {
    setValidations({});
    if (isOtherPerson) {
      setPostData(newPostData => ({
        ...newPostData,
        customerCompanyName: '',
        contactName: '',
        contactEmail: ''
      }));
    } else if (userData) {
      setPostData(newPostData => ({
        ...newPostData,
        customerCompanyName: userData.company,
        contactName: userData.firstName + ' ' + userData.lastName,
        contactEmail: userData.email
      }));
    }
  }, [isOtherPerson]);
  // Change request type
  React.useEffect(() => {
    setValidations({});
  }, [postData.requestType]);

  const restartProcess = () => {
    setPostData({
      ...initPostData,
      customerId: userData.company || 'B2C dummy',
      customerCompanyName: userData.company,
      accountName: userData.company,
      zip: userData.address?.postalCode || userData.address?.postalCode || '',
      contactName: userData.firstName + ' ' + userData.lastName,
      contactEmail: userData.email,
      affectedEmailAddress: userData.email,
      ava_endcustomer: userData.firstName + ' ' + userData.lastName
    });
    setFiles([]);
    setFileKey(Math.random().toString(36));
    setValidations({});
  };
  const validate = React.useCallback(async () => {
    // setValidations({});
    let _validations = {};
    if (!postData.ava_endcustomer) _validations = { ..._validations, ava_endcustomer: true };
    if (isOtherPerson) {
      if (!postData.contactName) _validations = { ..._validations, contactName: true };
      if (!postData.contactEmail || !VALIDATE_REGEX.email.test(postData.contactEmail))
        _validations = { ..._validations, contactEmail: true };
    }
    if (postData.attachments && validations.attachments) {
      _validations = { ..._validations, attachments: validations.attachments };
    }

    switch (postData.requestType) {
      case 'service':
        // STEP 1
        if (!postData.typeOfProblem) {
          if (!postData.title) {
            _validations = { ..._validations, title: true };
          }
          if (!postData.impact) {
            _validations = { ..._validations, impact: true };
          }
          if (!postData.urgency) {
            _validations = { ..._validations, urgency: true };
          }
        }
        // STEP 2
        if (postData.typeOfProblem) {
          if (!postData.component) {
            _validations = { ..._validations, component: true };
          }
          if (!postData.customerCompanyName) {
            _validations = { ..._validations, customerCompanyName: true };
          }
          if (!postData.affectedEmailAddress || !VALIDATE_REGEX.email.test(postData.affectedEmailAddress)) {
            _validations = { ..._validations, affectedEmailAddress: true };
          }
          if (!postData.description) {
            _validations = { ..._validations, description: true };
          }

          if (postData.component) {
            if (
              postData.typeOfProblem === 'VDO Fleet' &&
              postData.component !== 'VDO Fleet App' &&
              !postData.affectedDriver
            ) {
              _validations = { ..._validations, affectedDriver: true };
            }
            if (postData.typeOfProblem === 'Hardware' && !postData.deviceserialnumber) {
              _validations = { ..._validations, deviceserialnumber: true };
            }
          }
        }
        break;
      case 'sales':
      case 'logistics':
        if (!postData.customerCompanyName) {
          _validations = { ..._validations, customerCompanyName: true };
        }
        if (!postData.affectedEmailAddress || !VALIDATE_REGEX.email.test(postData.affectedEmailAddress)) {
          _validations = { ..._validations, affectedEmailAddress: true };
        }
        if (!postData.title) {
          _validations = { ..._validations, title: true };
        }
        if (!postData.description) {
          _validations = { ..._validations, description: true };
        }
        break;
      default:
        break;
    }
    setValidations(_validations);
    return _validations;
  }, [postData, validations]);

  const updatePostData = (name, value) => {
    setPostData(newPostData => ({ ...newPostData, [name]: value }));
    if (name === 'requestType' && value === 'service') {
      setPostData(newPostData => ({
        ...newPostData,
        component: null,
        urgency: null,
        impact: null
      }));
    }
    if (name === 'requestType' && value === 'logistics') {
      setPostData(newPostData => ({
        ...newPostData,
        component: 'Logistics/Accounting request',
        urgency: 'low',
        impact: 'minor'
      }));
    }
    if (name === 'requestType' && value === 'sales') {
      setPostData(newPostData => ({
        ...newPostData,
        component: 'Sales request',
        urgency: 'low',
        impact: 'minor'
      }));
    }
    // Validation after update
    if (
      name === 'customerCompanyName' ||
      name === 'ava_endcustomer' ||
      name === 'contactName' ||
      name === 'title' ||
      name === 'title' ||
      name === 'impact' ||
      name === 'urgency' ||
      name === 'description' ||
      (name === 'deviceserialnumber' && postData.typeOfProblem === 'Hardware') ||
      (name === 'affectedDriver' && postData.typeOfProblem === 'VDO Fleet')
    ) {
      setValidations(validations => ({
        ...validations,
        [name]: value === ''
      }));
    }
    if (name === 'contactEmail' || name == 'affectedEmailAddress') {
      setValidations(validations => ({
        ...validations,
        [name]: value === '' || !VALIDATE_REGEX.email.test(value)
      }));
    }
  };
  const closeNewTicket = () => {
    if (!updateTicketLoading) {
      onClose(false);
      setCancelModalOpen(false);

      // avoid flash of layout
      setTimeout(() => {
        setPostData(initPostData);
        setValidations({});
        setError(false);
        setSuccess(false);
        setFiles([]);
        dispatch(clearTicketError());
      }, 300);
    }
  };
  const handleClose = () => {
    if (!updateTicketLoading) {
      if (success) closeNewTicket();
      else setCancelModalOpen(true);
    }
  };
  const goToStep2 = async typeOfProblem => {
    const _validations = await validate();
    if (Object.keys(_validations).length === 0) {
      updatePostData('typeOfProblem', typeOfProblem);
    }
  };
  const handleSubmit = async () => {
    const _validations = await validate();

    if (Object.keys(_validations).length === 0 || Object.values(_validations).every(item => !item)) {
      const componentMapping = {
        'Telematic Services': {
          project: 'Telematic Services'
        },
        'Partner API': {
          project: 'Partner API'
        }
        // 'Local Products': {
        //   project: 'Telematic Services'
        // }
      };
      const mapping = componentMapping[postData.component];
      const project = mapping && mapping.project ? mapping.project : postData.component;
      const components = mapping && mapping.components ? mapping.components : postData.component;

      const createTicketPostData = {
        title: postData.title,
        description: postData.description,
        customerid: postData.customerId,
        accountName: postData.accountName,
        zip: postData.zip,
        contactName: postData.contactName || postData.ava_endcustomer,
        contactEmail: postData.contactEmail,
        project: project,
        impact: postData.impact,
        urgency: postData.urgency,
        smy_affecteddriver: postData.affectedDriver || null,
        smy_Project: null,
        smy_SubProject: null,
        smy_deviceserialnumber: postData.deviceserialnumber,
        smy_companycardnumber: postData.companyCardNumber,
        smy_model: postData.model,
        smy_softwareversion: postData.softwareVersion,
        components: components,
        ava_endcustomer: isOtherPerson ? postData.ava_endcustomer : null,
        ava_endcustomercontact: isOtherPerson ? postData.affectedEmailAddress : null,
        attachments: postData.attachments?.length > 0 ? postData.attachments : null,
        attachmentFileNames: postData.attachmentFileNames?.length > 0 ? postData.attachmentFileNames : null,
        vrnnumber: postData.component === 'Motion/Maps' ? postData.affectedVehicle : postData.vehicleRegistrationNumber
      };
      await dispatch(createTicket(createTicketPostData));
      setError(false);
      setSuccess(true);

      if (window.trackDataLayer) {
        window.trackDataLayer(
          'ticket_created',
          {
            ticket_details: {
              request_type: requestType[postData.requestType],
              impact: postData.impact,
              urgency: postData.urgency,
              ticket_scope:
                postData.requestType === 'service' ? postData.typeOfProblem : requestType[postData.requestType],
              component: postData.requestType === 'service' ? postData.component : 'N/A'
            }
          },
          user
        );
      }

      setTimeout(() => {
        dispatch(getTickets());
        dispatch(getCompanyTickets());
      }, 1000);
    }
  };

  return (
    <>
      <Dialog
        className={`ticket-modal-dialog`}
        fullWidth
        maxWidth="md"
        open={open}
        PaperProps={{
          style: {
            backgroundColor: '#fff',
            borderRadius: 10
          }
        }}
        onClose={handleClose}
        aria-labelledby="max-width-dialog-title"
      >
        <DialogActions>
          <IconButton onClick={handleClose} disabled={updateTicketLoading}>
            <CloseIcon style={{ color: '#000' }} />
          </IconButton>
        </DialogActions>
        <DialogContent>
          <div className="create-ticket-body">
            {errorTicket ? (
              <ErrorCreateTicket closeModal={closeNewTicket} />
            ) : error ? (
              <ErrorCreateTicket closeModal={closeNewTicket} />
            ) : success ? (
              <SuccessCreateTicket closeModal={closeNewTicket} />
            ) : (
              <>
                <Typography className="create-ticket-title">{t('Tickets.newTicket')}</Typography>
                <Box className="create-ticket-box">
                  {updateTicketLoading && <UpdateTicketLoading />}
                  {postData.typeOfProblem === null && (
                    <>
                      <div className="contact-person-heading">{t('Tickets.contactPerson')}</div>
                      {user && (
                        <div className="contact-person">
                          <div className="contact-person-left">
                            <p className="contact-name">
                              {userData.firstName} {userData.lastName}
                            </p>
                            <p className="contact-email">{userData.email}</p>
                            <p className="contact-tel">{userData.address?.phoneNumber || ''}</p>
                          </div>
                          <div className="contact-person-right">
                            <p className="contact-address">{userData.company}</p>
                            {userData.address && (
                              <>
                                <p className="contact-street">
                                  {userData.address.street} {userData.address.postBox}
                                </p>
                                <p className="contact-city">
                                  {userData.address.country} {userData.address.postalCode} {userData.address.city}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginTop: 12, marginBottom: 12 }}>
                            <SelectComponent
                              // disabled={updateTicketLoading}
                              options={[
                                { value: 'service', name: requestType['service'] },
                                { value: 'sales', name: requestType['sales'] },
                                { value: 'logistics', name: requestType['logistics'] }
                              ]}
                              onChange={e => updatePostData('requestType', e.target.value)}
                              value={postData.requestType}
                              label={`${t('Tickets.requestType')} *`}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {postData.requestType && postData.requestType === 'service' ? (
                        <>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.title')} *`}
                                  onChange={e => updatePostData('title', e.target.value)}
                                  value={postData.title || ''}
                                  error={validations.title || null}
                                  aria-describedby="component-helper-text"
                                  // helperText={postData.title ? `${postData.title.length}/200` : ``}
                                  inputProps={{ maxLength: 200 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12, position: 'relative' }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={t('Tickets.dealerAssociation')}
                                  onChange={e => updatePostData('dealerAssociation', e.target.value)}
                                  value={postData.dealerAssociation || ''}
                                />
                                {/* This is an example */}
                                <Grid
                                  style={{
                                    position: 'absolute',
                                    zIndex: 2,
                                    right: 0,
                                    bottom: 0
                                  }}
                                >
                                  <Tooltip content={t('Tickets.dealerAssociationTooltip')} placement="left" />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <SelectComponent
                                  label={`${t('Tickets.impact')} *`}
                                  options={impactData}
                                  onChange={e => updatePostData('impact', e.target.value)}
                                  value={postData.impact}
                                  error={validations.impact || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <SelectComponent
                                  label={`${t('Tickets.urgency')} *`}
                                  options={urgencyData}
                                  onChange={e => updatePostData('urgency', e.target.value)}
                                  value={postData.urgency}
                                  error={validations.urgency || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          {postData.typeOfProblem === null && (
                            <div className="create-ticket-btns-step-1">
                              <Button borderOnly onClick={() => goToStep2('VDO Fleet')}>
                                VDO Fleet
                              </Button>
                              <Button borderOnly onClick={() => goToStep2('Hardware')}>
                                Hardware
                              </Button>
                            </div>
                          )}
                        </>
                      ) : postData.requestType && postData.requestType !== 'service' ? (
                        <>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  disabled={!isOtherPerson}
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.customerCompanyName')} *`}
                                  onChange={e => updatePostData('customerCompanyName', e.target.value)}
                                  value={postData.customerCompanyName || ''}
                                  error={validations.customerCompanyName || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  // disabled={updateTicketLoading}
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.name')} *`}
                                  onChange={e => updatePostData('ava_endcustomer', e.target.value)}
                                  value={postData.ava_endcustomer || ''}
                                  error={validations.ava_endcustomer || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  // disabled={updateTicketLoading}
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.affectedEmailAddress')} *`}
                                  onChange={e => updatePostData('affectedEmailAddress', e.target.value)}
                                  value={postData.affectedEmailAddress || ''}
                                  error={validations.affectedEmailAddress || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  // disabled={updateTicketLoading}
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.title')} *`}
                                  onChange={e => updatePostData('title', e.target.value)}
                                  value={postData.title || ''}
                                  error={validations.title || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 30 }}>
                                <TextField
                                  className="create-ticket-textarea"
                                  multiline
                                  // disabled={updateTicketLoading}
                                  label={`${t('Tickets.description')} *`}
                                  onChange={e => updatePostData('description', e.target.value)}
                                  value={postData.description || ''}
                                  variant="outlined"
                                  minRows={4}
                                  error={validations.description || null}
                                  // helperText={postData.description ? `${postData.description.length}/100000` : ``}
                                  inputProps={{ maxLength: 100000 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </>
                      ) : null}
                    </>
                  )}
                  {/* STEP 2 */}
                  {postData.typeOfProblem !== null && (
                    <>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginBottom: 12 }}>
                            <SelectComponent
                              // disabled={updateTicketLoading}
                              label={`${t('Tickets.component')} *`}
                              options={componentData}
                              onChange={e => updatePostData('component', e.target.value)}
                              value={postData.component}
                              error={validations.component || null}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginBottom: 12 }}>
                            <TextField
                              disabled={!isOtherPerson}
                              className="create-ticket-text-field"
                              label={`${t('Tickets.customerCompanyName')} *`}
                              onChange={e => updatePostData('customerCompanyName', e.target.value)}
                              value={postData.customerCompanyName || ''}
                              error={validations.customerCompanyName || null}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginBottom: 12 }}>
                            <TextField
                              // disabled={updateTicketLoading}
                              className="create-ticket-text-field"
                              label={`${t('Tickets.affectedEmailAddress')} *`}
                              onChange={e => updatePostData('affectedEmailAddress', e.target.value)}
                              value={postData.affectedEmailAddress || ''}
                              error={validations.affectedEmailAddress || null}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid style={{ marginBottom: 30 }}>
                            <TextField
                              className="create-ticket-textarea"
                              multiline
                              // disabled={updateTicketLoading}
                              label={`${t('Tickets.description')} *`}
                              onChange={e => updatePostData('description', e.target.value)}
                              value={postData.description || ''}
                              variant="outlined"
                              minRows={4}
                              error={validations.description || null}
                              // helperText={postData.description ? `${postData.description.length}/100000` : ``}
                              inputProps={{ maxLength: 100000 }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <UploadField
                        dataValue={postData.attachments}
                        accept={`application/pdf, image/*`}
                        type={`screenshot`}
                        error={validations.attachments}
                        filesLimit={MAX_NUMBER_OF_FILES}
                        setValidations={setValidations}
                        setPostData={setPostData}
                        postData={postData}
                        fileKey={fileKey}
                      />
                      {postData.component && postData.component !== 'VDO Fleet App' && (
                        <>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={t('Tickets.vehicleRegistrationNumber')}
                                  onChange={e => updatePostData('vehicleRegistrationNumber', e.target.value)}
                                  value={postData.vehicleRegistrationNumber || ''}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.affectedDriver')}${
                                    postData.typeOfProblem === 'VDO Fleet' ? ` *` : ``
                                  }`}
                                  onChange={e => updatePostData('affectedDriver', e.target.value)}
                                  value={postData.affectedDriver || ''}
                                  error={validations.affectedDriver || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={t('Tickets.companyCardNumber')}
                                  onChange={e => updatePostData('companyCardNumber', e.target.value)}
                                  value={postData.companyCardNumber || ''}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid item xs={12}>
                              <Grid style={{ marginBottom: 12 }}>
                                <TextField
                                  className="create-ticket-text-field"
                                  label={`${t('Tickets.deviceSerialNumber')}${postData.typeOfProblem === 'Hardware' ? ` *` : ``}`}
                                  onChange={e => updatePostData('deviceserialnumber', e.target.value)}
                                  value={postData.deviceserialnumber || ''}
                                  error={validations.deviceserialnumber || null}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Box>
                {postData.typeOfProblem !== null || (postData.requestType && postData.requestType !== 'service') ? (
                  <div className="create-ticket-btns">
                    {postData.requestType && postData.requestType === 'service' && (
                      <>
                        <button disabled={updateTicketLoading} className="btn-restart" onClick={restartProcess}>
                          {t('Tickets.restartProcess')}
                        </button>
                        <Button
                          borderOnly
                          noArrow
                          disabled={updateTicketLoading}
                          onClick={() => {
                            setPostData({
                              ...postData,
                              typeOfProblem: null,
                              component: null,
                              affectedDriver: null,
                              deviceserialnumber: null,
                              companyCardNumber: null,
                              vehicleRegistrationNumber: null,
                              affectedVehicle: null,
                              attachments: null,
                              attachmentFileNames: null
                            });
                            setValidations({});
                            setFiles([]);
                          }}
                        >
                          {t('Tickets.back')}
                        </Button>
                      </>
                    )}
                    <Button
                      disabled={updateTicketLoading}
                      borderOnly
                      onClick={handleSubmit}
                      additionalClassName="btn-submit"
                    >
                      {updateTicketLoading ? t('personalData.loading') : t('Tickets.submit')}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <CancelCreateTicketModal
        onClose={() => setCancelModalOpen(false)}
        open={cancelModalOpen}
        closeNewTicket={closeNewTicket}
      />
    </>
  );
}
